// Contracts — kids SESSION V1 (2026-06-29). A moderator who already shot on their
// phone records the participant's appearance via a simple form → a `done` session
// is created in D1 (synthetic parent/meeting/child, mirrors handleManualSession but
// WITHOUT clips/captures). The packer joins this metadata to the shipped video by
// PARTICIPANT NAME ("First Last"), so the name is two latin-only fields, normalized
// (trim + collapse inner whitespace) on the worker so the D1 row matches the VPS
// folder `input/<First Last>/` byte-for-byte.
//
// Two latin-only name parts (not one free field): the upload folder name is built
// from the same two parts, so splitting forces both sides to agree on the format.
import { z } from 'zod';
import { KidsGenderSchema } from '../kids';
import { KidsLightingSchema, KidsFaceExpressionSchema } from './enums';

// Latin letters + space, apostrophe, hyphen (e.g. "O'Brien", "Jean-Luc"); must
// start with a letter. NOT KidsEthnicitySchema-strict — ethnicity stays a free
// string here (the form offers ETHNICITIES but the column is plain TEXT).
const NAME_RE = /^[A-Za-z][A-Za-z '\-]*$/;
const namePart = z.string().trim().min(1).max(60).regex(NAME_RE);

// One lighting reading collected by the Session V1 form. lux is the meter reading
// (must be > 0; the form may flag out-of-range via validateLux but never blocks).
// faceExpression is the canonical 12-value slug. These become `captures` rows on
// the worker (one per lighting) so the packer gets the metadata it joins on.
export const KidsSessionLightSchema = z.object({
  lighting: KidsLightingSchema,
  lux: z.number().int().positive(),
  faceExpression: KidsFaceExpressionSchema,
});
export type KidsSessionLight = z.infer<typeof KidsSessionLightSchema>;

export const KidsSessionCreateReqSchema = z.object({
  firstName: namePart,
  lastName: namePart,
  // Parent's name (Tim 05.07 — the moderator form must capture it; lands on the
  // synthetic parent user). OPTIONAL at the contract layer so stale cached PWA
  // bundles keep working mid-rollout — the CURRENT form requires both fields.
  parentFirstName: namePart.optional(),
  parentLastName: namePart.optional(),
  age: z.number().int().min(5).max(17),
  gender: KidsGenderSchema,
  ethnicity: z.string(),
  skinTone: z.number().int().min(1).max(10),
  hasBeard: z.boolean(),
  hasMustache: z.boolean(),
  usesReadingGlasses: z.boolean(),
  accessoryName: z.string().max(120).nullable().optional(),
  // Optional participant reference photo (#71): a small client-downscaled JPEG
  // dataURL (maxSide 640, q 0.7). Capped at 2MB of base64 so a rogue payload can't
  // balloon the create body; a bad/absent photo never fails the session create (the
  // worker stores it best-effort AFTER the child INSERT). Same cap as the shoot's
  // KidsParticipantPhotoReqSchema. The photo bytes are PII — never logged.
  photo: z.string().max(2_000_000).optional(),
  // faceval (#34): id of the validate job this form's photo passed through. When
  // present, the worker AUTO-enqueues an `add` job after the create succeeds — the
  // face lands in the mini's intake base immediately (согласовано 16.07: сессия
  // реальна ⇒ лицо реально; кнопка-выбор из формы убрана, авто). Best-effort: a
  // missing/stale job never fails the create.
  facevalJobId: z.string().optional(),
  // Exactly 4 lights, one per DISTINCT LIGHTING (the form renders all four in
  // fixed order). Distinctness enforced here — the worker silently de-dupes, so
  // without this a malformed client would get a done-session with <4 captures.
  lights: z
    .array(KidsSessionLightSchema)
    .length(4)
    .superRefine((lights, ctx) => {
      if (new Set(lights.map((l) => l.lighting)).size !== lights.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'duplicate lighting' });
      }
    }),
});
export type KidsSessionCreateReq = z.infer<typeof KidsSessionCreateReqSchema>;

export const KidsSessionCreateResSchema = z.object({
  ok: z.literal(true),
  sessionId: z.number().int(),
  pid: z.string().nullable(), // moderator's 2-digit tag (null until assigned)
  name: z.string(), // normalized "First Last" — what the packer matches on
});
export type KidsSessionCreateRes = z.infer<typeof KidsSessionCreateResSchema>;

// ── Namesake pre-check (soft warning, 2026-07-07) ─────────────────────────────
//   POST /api/kids/session/check-name  { firstName, lastName } → { exists, candidates }
// The moderator form has NO phone-number / unique key, so a second participant
// with the SAME "First Last" is legitimate BUT usually a mistake (re-recording an
// already-created child). On lastName blur / before submit the form asks whether
// a namesake already exists in the caller's office; if so it shows a soft yellow
// warning card («Участник с похожим именем уже существует») with «Всё равно
// создать». This is NOT a block — creation is always allowed. Names are PII →
// JSON body only (never the URL/log), same latin "First Last" the create accepts.
export const KidsSessionCheckNameReqSchema = z.object({
  firstName: namePart,
  lastName: namePart,
});
export type KidsSessionCheckNameReq = z.infer<typeof KidsSessionCheckNameReqSchema>;

// One existing namesake surfaced to the moderator. `name` is the canonical stored
// children.name; age/office help tell two same-name children apart. age is
// nullable (children.age is a nullable column); office is nullable (a child whose
// meeting has no office on file).
export const KidsSessionCheckNameCandidateSchema = z.object({
  name: z.string(),
  age: z.number().int().nullable(),
  office: z.string().nullable(),
});
export type KidsSessionCheckNameCandidate = z.infer<typeof KidsSessionCheckNameCandidateSchema>;

export const KidsSessionCheckNameResSchema = z.object({
  exists: z.boolean(),
  candidates: z.array(KidsSessionCheckNameCandidateSchema),
});
export type KidsSessionCheckNameRes = z.infer<typeof KidsSessionCheckNameResSchema>;
